/**
 * SmartInventoryFeatures — public marketing block for FoodPulse Smart Inventory.
 *
 * Rendered:
 *   1. As the signed-out state of `/food-pulse/owner/inventory`
 *   2. As a standalone `/food-pulse/inventory` landing page
 *
 * Content matches the actually-shipped backend (`fp_smart_inventory.py`,
 * `fp_deal_stream.py`, `fp_supplier_feeds.py`, `foodpulse_impact.py`,
 * `splink.py`) — every capability listed here has a working endpoint.
 */
import { Link } from "react-router-dom";
import {
  PackagePlus, ScanLine, QrCode, AlertTriangle, Sparkles, Tag,
  Gift, ArrowLeftRight, Radio, MapPin, Building2, BarChart3,
  Recycle, Camera, Cloud, ArrowRight, CheckCircle2,
} from "lucide-react";

const GREEN  = "#0F3D2E";
const CREAM  = "#FBF7EF";
const AMBER  = "#E4A140";
const PURPLE = "#7C3AED";

const FEATURES = [
  {
    icon: PackagePlus,
    title: "Lot-first receiving",
    body: "Log every incoming lot with GTIN, expiry, quantity, unit cost, and supplier — under 30 seconds per lot. Perfect for perishables where two crates of the same SKU can have very different lifespans.",
  },
  {
    icon: ScanLine,
    title: "Barcode & GS1 scanner",
    body: "Scan any GTIN-8, UPC, EAN, or GS1-128 label. Known products auto-fill; unknown codes create a pending master record so nothing blocks receiving.",
  },
  {
    icon: QrCode,
    title: "SpLink lot QR codes",
    body: "Every lot gets a scannable QR label instantly. Staff scan it to adjust quantity, inspect condition, or promote to a deal without hunting through menus.",
  },
  {
    icon: Camera,
    title: "AI freshness check",
    body: "Snap a baseline photo at receiving and a follow-up any time. Gemini estimates a freshness delta and flags lots that need to move today.",
    badge: "AI",
  },
  {
    icon: AlertTriangle,
    title: "Daily Action Center",
    body: "One glance shows every at-risk lot ranked by days-to-expiry and condition, with the recommended action pre-selected — discount, surprise box, donation, or transfer.",
    badge: "Waste-first",
  },
  {
    icon: Tag,
    title: "One-tap promote to Deal",
    body: "Turn any lot into a live public deal in one tap. Discount %, ends-at, and cover copy are pre-filled — the deal appears on FoodPulse instantly.",
  },
  {
    icon: Gift,
    title: "Surprise Box & Donation",
    body: "Bundle multiple near-expiry lots into a Surprise Box, or route them to a partner food bank. Donations automatically log to your Impact page.",
  },
  {
    icon: ArrowLeftRight,
    title: "Between-store transfers",
    body: "Move stock from one location to another and the ledger updates on both sides. Ideal for multi-branch grocers rebalancing inventory before expiry.",
  },
  {
    icon: Radio,
    title: "Real-time GPS deal push",
    body: "The moment you promote a lot, nearby shoppers with the FoodPulse app get a live SSE push. No cold notifications, no delay.",
    badge: "Live",
  },
  {
    icon: Cloud,
    title: "Supplier feed ingestion",
    body: "Large distributors and wholesalers can push daily deals via JSON pull, CSV pull, or signed HMAC webhook. Optional staging queue lets your team review before it goes live.",
  },
  {
    icon: BarChart3,
    title: "30-day rollup dashboard",
    body: "See active lots, closed lots, spoiled quantity, and donated quantity for every business you manage — always powered by the append-only ledger so numbers never drift.",
  },
  {
    icon: MapPin,
    title: "Impact map integration",
    body: "Every donation and rescue automatically shows on the public Save-the-Food impact map, giving your business measurable community credit.",
  },
  {
    icon: Building2,
    title: "Multi-business ready",
    body: "One owner login can manage inventory for every claimed location. Role-based access lets you add managers and floor staff without sharing credentials.",
  },
  {
    icon: Recycle,
    title: "Append-only ledger",
    body: "Every quantity change is a ledger entry — never a destructive update. Full audit trail for tax, food-safety inspections, and internal shrink analysis.",
    badge: "Audit-safe",
  },
];

const FLOW = [
  { step: "1", label: "Receive lot",       hint: "Scan GTIN · set expiry · attach baseline photo" },
  { step: "2", label: "Get SpLink QR",     hint: "Auto-printed label ties every action to this lot" },
  { step: "3", label: "Track & inspect",   hint: "Adjust qty, recheck freshness with AI any time" },
  { step: "4", label: "Action Center",     hint: "At-risk lots surface with the right next step" },
  { step: "5", label: "One-tap promote",   hint: "Deal · Surprise Box · Donation · Transfer" },
  { step: "6", label: "Reach shoppers",    hint: "Live GPS push + map + digest — no ads needed" },
];

function FeatureCard({ icon: Icon, title, body, badge }) {
  return (
    <div className="rounded-2xl border bg-white p-5 hover:shadow-md transition" style={{ borderColor: "#E9D8B4" }} data-testid={`feature-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="rounded-full p-2" style={{ background: "#F0E2C7" }}>
          <Icon size={20} style={{ color: GREEN }} />
        </div>
        {badge && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: PURPLE, color: "white" }}>{badge}</span>}
      </div>
      <div className="text-sm font-bold" style={{ color: GREEN }}>{title}</div>
      <p className="text-xs text-gray-700 mt-1 leading-relaxed">{body}</p>
    </div>
  );
}

function FlowStep({ step, label, hint, last }) {
  return (
    <div className="flex items-center gap-2 min-w-0 flex-1" data-testid={`flow-step-${step}`}>
      <div className="flex-1 rounded-2xl border bg-white p-3 min-w-0" style={{ borderColor: "#E9D8B4" }}>
        <div className="flex items-center gap-2">
          <span className="rounded-full w-6 h-6 flex items-center justify-center text-xs font-black text-white" style={{ background: GREEN }}>{step}</span>
          <div className="text-sm font-bold" style={{ color: GREEN }}>{label}</div>
        </div>
        <div className="text-[11px] text-gray-600 mt-1 leading-snug">{hint}</div>
      </div>
      {!last && <ArrowRight size={16} className="hidden md:block flex-shrink-0" style={{ color: AMBER }} />}
    </div>
  );
}

export default function SmartInventoryFeatures({ showHero = true, showCTAs = true }) {
  return (
    <div data-testid="smart-inventory-features">
      {showHero && (
        <section className="rounded-3xl p-8 md:p-10 mb-8" style={{ background: "linear-gradient(135deg,#F0E2C7,#FEF3C7)" }}>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} style={{ color: PURPLE }} />
            <span className="text-xs uppercase tracking-widest font-bold" style={{ color: PURPLE }}>Smart Inventory · for food businesses</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black leading-tight" style={{ color: GREEN }}>
            Track every lot. Rescue every dollar. <br className="hidden md:block" />
            <span style={{ color: AMBER }}>Waste less, sell more.</span>
          </h1>
          <p className="text-sm md:text-base text-gray-800 mt-4 max-w-2xl leading-relaxed">
            A lot-first inventory system built for bakeries, grocers, farms, and restaurants
            who can't afford to write off perishables. Receive in 30 seconds, get AI freshness
            alerts, and turn at-risk stock into a live deal with a single tap.
          </p>
          {showCTAs && (
            <div className="flex flex-wrap gap-2 mt-5">
              <Link to="/food-pulse/business/signup" className="rounded-full px-5 py-2 text-sm font-bold" style={{ background: GREEN, color: CREAM }} data-testid="cta-signup">
                List my business →
              </Link>
              <Link to="/food-pulse/claim" className="rounded-full px-5 py-2 text-sm font-bold border" style={{ borderColor: GREEN, color: GREEN }} data-testid="cta-claim">
                Claim an existing listing
              </Link>
              <a href="#how-it-works" className="rounded-full px-5 py-2 text-sm font-bold" style={{ color: PURPLE }} data-testid="cta-how">
                See how it works ↓
              </a>
            </div>
          )}
        </section>
      )}

      <section id="how-it-works" className="mb-8" data-testid="section-flow">
        <h2 className="text-xl md:text-2xl font-black mb-4" style={{ color: GREEN }}>How it works · 6 steps, one afternoon to onboard</h2>
        <div className="flex flex-col md:flex-row gap-2">
          {FLOW.map((s, i) => (
            <FlowStep key={s.step} {...s} last={i === FLOW.length - 1} />
          ))}
        </div>
      </section>

      <section className="mb-8" data-testid="section-features">
        <h2 className="text-xl md:text-2xl font-black mb-4" style={{ color: GREEN }}>Everything included</h2>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => <FeatureCard key={f.title} {...f} />)}
        </div>
      </section>

      <section className="rounded-3xl border p-6 md:p-8" style={{ borderColor: "#E9D8B4", background: "white" }} data-testid="section-outcomes">
        <h2 className="text-xl md:text-2xl font-black mb-4" style={{ color: GREEN }}>What businesses get out of it</h2>
        <ul className="grid gap-2 md:grid-cols-2 text-sm text-gray-800">
          {[
            "Fewer write-offs — at-risk lots move before they spoil.",
            "Higher margins on last-mile inventory via one-tap discounts.",
            "Verified donations that count for tax + ESG reporting.",
            "Free organic reach through GPS deal push and the impact map.",
            "Audit-safe ledger for food-safety and shrink reviews.",
            "One dashboard for every location you claim.",
          ].map((o) => (
            <li key={o} className="flex items-start gap-2">
              <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" style={{ color: GREEN }} /> {o}
            </li>
          ))}
        </ul>
        {showCTAs && (
          <div className="mt-6 flex flex-wrap gap-2">
            <Link to="/food-pulse/business/signup" className="rounded-full px-5 py-2 text-sm font-bold" style={{ background: PURPLE, color: "white" }} data-testid="cta-signup-bottom">
              Get started free →
            </Link>
            <Link to="/food-pulse" className="rounded-full px-5 py-2 text-sm font-bold border" style={{ borderColor: GREEN, color: GREEN }} data-testid="cta-back-fp">
              Back to FoodPulse
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
